/**
 * Command executor. The ONLY place simulation state changes from player/bot
 * input. Validates ownership and feasibility, then mutates the world.
 *
 * Humans and bots produce the exact same Command shapes — that's what makes
 * bots trivial and future lockstep MP cheap (server just relays these).
 */
import type { World } from "./world";
import type { Registry } from "../data/registry";
import type { Command } from "./types";
import { setOrder } from "./orders";
import { ejectGarrison } from "./systems/transport";
import { spawnBuilding } from "./spawn";
import { canGatherFrom } from "./economy";
import { isNaval, nearestWaterEdge } from "./naval";
import type { BuildingDef } from "../data/schema";

/** Foundations are destroyed at the end of the tick, so remember those already
 * cancelled during that gap and never refund one twice. */
const cancelledFoundations = new WeakMap<World, Set<number>>();

/** Queue positions originate from a rendered queue snapshot. A rapid duplicate
 * click must not retarget the item that shifts into that slot before the next
 * simulation tick. */
const cancelledTrainSlots = new WeakMap<World, { tick: number; slots: Set<string> }>();

function owns(world: World, player: number, eid: number): boolean {
  return world.owner.get(eid)?.player === player;
}

/** Whether a building of `def` may be placed on hex (col,row). Docks go on a
 *  water edge; everything else on a free, buildable LAND hex. */
export function canPlaceBuilding(world: World, def: BuildingDef, col: number, row: number): boolean {
  if (!world.inBounds(col, row)) return false;
  if (def.placement === "waterEdge") {
    if (!world.isWaterEdge(col, row)) return false;
    // water is already "blocked", so guard against stacking docks on one hex
    for (const [, b] of world.building)
      if (b.origin.x === col && b.origin.y === row) return false;
    return true;
  }
  return world.isWalkable(col, row);
}

/** Total population already committed in every production queue of a player. */
function queuedPop(world: World, reg: Registry, player: number): number {
  let total = 0;
  for (const [bid, prod] of world.producer) {
    if (world.owner.get(bid)?.player !== player) continue;
    const civ = world.ident.get(bid)?.civ;
    if (!civ) continue;
    for (const item of prod.queue) total += reg.unit(civ, item.unitId).population;
  }
  return total;
}

/** Attach the per-world helper functions the gather/production systems read off
 *  `world`. MUST run on every world (fresh OR restored) before stepping — else a
 *  loaded game's workers can't find a drop-site and never resume gathering. */
export function attachWorldHelpers(world: World): void {
  (world as unknown as { _nearestDropSite: unknown })._nearestDropSite = nearestDropSite;
}

/** nearest drop-site for a player relative to a position. `gatherer` (when given)
 *  scopes the search to drop-sites that unit can actually reach: a fishing BOAT
 *  (naval) only unloads at a DOCK (a drop-site on water), never an inland town
 *  centre it would have to beach itself to reach. */
function nearestDropSite(world: World, player: number, x: number, y: number, gatherer?: number): number | null {
  const naval = gatherer !== undefined && isNaval(world, gatherer);
  let best: number | null = null;
  let bestD = Infinity;
  const set = world.dropsitesByPlayer.get(player);
  if (!set) return null;
  for (const eid of set) {
    const tr = world.transform.get(eid);
    const b = world.building.get(eid);
    if (!tr || !b || b.isFoundation) continue;
    if (naval && !world.isWater(b.origin.x, b.origin.y)) continue; // ships → docks only
    const d = (tr.pos.x - x) ** 2 + (tr.pos.y - y) ** 2;
    // tie-break by lower eid so drop-site choice is iteration-order-independent.
    if (d < bestD || (d === bestD && eid < (best ?? Infinity))) {
      bestD = d;
      best = eid;
    }
  }
  return best;
}

export function executeCommand(world: World, reg: Registry, cmd: Command): void {
  switch (cmd.type) {
    case "move": {
      for (const eid of cmd.entities)
        if (owns(world, cmd.player, eid))
          setOrder(world, eid, { kind: "move", x: cmd.x, y: cmd.y }, !!cmd.queued);
      break;
    }
    case "attackMove": {
      for (const eid of cmd.entities)
        if (owns(world, cmd.player, eid))
          setOrder(world, eid, { kind: "attackMove", x: cmd.x, y: cmd.y }, !!cmd.queued);
      break;
    }
    case "attack": {
      for (const eid of cmd.entities)
        if (owns(world, cmd.player, eid))
          setOrder(world, eid, { kind: "attack", target: cmd.target }, !!cmd.queued);
      break;
    }
    case "stop": {
      // "cancel whatever you're doing": drop the path/order AND release any
      // gather/build assignment so the unit truly goes idle (not just pauses).
      for (const eid of cmd.entities) {
        if (!owns(world, cmd.player, eid)) continue;
        const mv = world.move.get(eid);
        if (mv) {
          mv.path = [];
          mv.intent = undefined;
          mv.queue = [];
          mv.guard = { ...(world.transform.get(eid)?.pos ?? { x: 0, y: 0 }) };
        }
        const bd = world.builder.get(eid);
        if (bd) bd.foundation = null;
        const g = world.gatherer.get(eid);
        if (g) { g.node = null; g.state = "toResource"; }
      }
      break;
    }
    case "gather": {
      for (const eid of cmd.entities) {
        if (!owns(world, cmd.player, eid) || !world.gatherer.has(eid)) continue;
        // capped nodes (fields) only take a fixed number of farmers — extra
        // workers ordered onto a full field are ignored rather than overstaffing.
        if (!canGatherFrom(world, cmd.target, eid)) continue;
        setOrder(world, eid, { kind: "gather", node: cmd.target }, !!cmd.queued);
      }
      break;
    }
    case "returnResources": {
      // send carrying workers to deposit at a specific drop-off building, then
      // go idle (does NOT auto-resume gathering).
      for (const eid of cmd.entities) {
        if (!owns(world, cmd.player, eid)) continue;
        const g = world.gatherer.get(eid);
        if (!g || (g.carrying <= 0 && !cmd.queued)) continue;
        const dropSite = world.building.get(cmd.target);
        if (!dropSite || dropSite.isFoundation || !dropSite.isDropSite) continue;
        if (!owns(world, cmd.player, cmd.target)) continue;
        if (!world.dropsitesByPlayer.get(cmd.player)?.has(cmd.target)) continue;
        setOrder(world, eid, { kind: "dropoff", dropSite: cmd.target }, !!cmd.queued);
      }
      break;
    }
    case "build": {
      const ps = world.players.get(cmd.player);
      if (!ps) break;
      const civ = reg.civ(ps.civ);
      if (!reg.data.buildings[cmd.buildingId] && !civ.buildings?.[cmd.buildingId]) break;
      const bDef = reg.building(ps.civ, cmd.buildingId);

      // snap world-space click to a hex; that hex must be a valid site for this
      // building type (land for most, a water edge for docks).
      const hex = world.hexAtWorld(cmd.x, cmd.y);
      if (!hex) break;
      if (!canPlaceBuilding(world, bDef, hex.col, hex.row)) break;

      // cost check
      let ok = true;
      for (const [r, amt] of Object.entries(bDef.cost)) {
        if ((ps.resources[r] ?? 0) < (amt ?? 0)) {
          ok = false;
          break;
        }
      }
      if (!ok) break;
      for (const [r, amt] of Object.entries(bDef.cost))
        ps.resources[r] -= amt ?? 0;

      const origin = { x: hex.col, y: hex.row };
      const foundation = spawnBuilding(
        world,
        reg,
        ps.civ,
        cmd.buildingId,
        cmd.player,
        origin,
        true
      );

      // assign workers (build is a queued task like any other order)
      for (const wid of cmd.workers) {
        if (!owns(world, cmd.player, wid) || !world.builder.has(wid)) continue;
        setOrder(world, wid, { kind: "build", foundation }, !!cmd.queued);
      }
      break;
    }
    case "construct": {
      // send workers to finish an already-placed foundation
      if (!owns(world, cmd.player, cmd.target)) break;
      const b = world.building.get(cmd.target);
      if (!b || !b.isFoundation) break;
      for (const wid of cmd.workers) {
        if (!owns(world, cmd.player, wid) || !world.builder.has(wid)) continue;
        setOrder(world, wid, { kind: "build", foundation: cmd.target }, !!cmd.queued);
      }
      break;
    }
    case "train": {
      if (!owns(world, cmd.player, cmd.building)) break;
      const ps = world.players.get(cmd.player);
      const prod = world.producer.get(cmd.building);
      const bCiv = world.ident.get(cmd.building)?.civ;
      if (!ps || !prod || !bCiv) break;
      // guard: the unit must belong to this building's civ — otherwise reg.unit
      // throws and the uncaught error freezes the whole sim loop.
      if (!reg.civ(bCiv).units[cmd.unitId]) break;
      const u = reg.unit(bCiv, cmd.unitId);
      // cost check
      for (const [r, amt] of Object.entries(u.cost))
        if ((ps.resources[r] ?? 0) < (amt ?? 0)) return;
      // pop cap check — count units ALREADY QUEUED across all of this player's
      // buildings, not just living ones, or you could queue 20 at 9/10 (each
      // passed because population hadn't risen yet) and end up at 30/10.
      if (ps.population + queuedPop(world, reg, cmd.player) + u.population > ps.populationCap) return;
      for (const [r, amt] of Object.entries(u.cost)) ps.resources[r] -= amt ?? 0;
      prod.queue.push({ unitId: cmd.unitId, time: u.buildTime, total: u.buildTime });
      break;
    }
    case "cancelTrain": {
      // remove a queued item and refund its resources (cost is deducted at
      // enqueue time; population is recomputed from living units, not reserved).
      if (!owns(world, cmd.player, cmd.building)) break;
      const prod = world.producer.get(cmd.building);
      const ps = world.players.get(cmd.player);
      const civ = world.ident.get(cmd.building)?.civ;
      if (!prod || !ps || !civ) break;
      let cancelled = cancelledTrainSlots.get(world);
      if (!cancelled || cancelled.tick !== world.tick) {
        cancelled = { tick: world.tick, slots: new Set<string>() };
        cancelledTrainSlots.set(world, cancelled);
      }
      const slot = `${cmd.building}:${cmd.index}`;
      if (cancelled.slots.has(slot)) break;
      const item = prod.queue[cmd.index];
      if (!item) break;
      const u = reg.unit(civ, item.unitId);
      for (const [r, amt] of Object.entries(u.cost))
        ps.resources[r] = (ps.resources[r] ?? 0) + (amt ?? 0);
      prod.queue.splice(cmd.index, 1);
      cancelled.slots.add(slot);
      break;
    }
    case "cancelConstruction": {
      // tear down an in-progress foundation and refund half its cost. Builders
      // assigned to it are released; the hex frees up on the next dead flush.
      if (!owns(world, cmd.player, cmd.building)) break;
      const b = world.building.get(cmd.building);
      const idn = world.ident.get(cmd.building);
      if (!b || !b.isFoundation || !idn?.civ) break;
      let cancelled = cancelledFoundations.get(world);
      if (!cancelled) {
        cancelled = new Set<number>();
        cancelledFoundations.set(world, cancelled);
      }
      if (cancelled.has(cmd.building)) break;
      const ps = world.players.get(cmd.player);
      if (ps) {
        const def = reg.building(idn.civ, idn.defId);
        for (const [r, amt] of Object.entries(def.cost))
          ps.resources[r] = (ps.resources[r] ?? 0) + Math.floor((amt ?? 0) * 0.5);
      }
      for (const [, bd] of world.builder) if (bd.foundation === cmd.building) bd.foundation = null;
      cancelled.add(cmd.building);
      world.markDead(cmd.building);
      break;
    }
    case "setRally": {
      if (!owns(world, cmd.player, cmd.building)) break;
      world.rally.set(cmd.building, { x: cmd.x, y: cmd.y, resource: cmd.resource });
      break;
    }
    case "embark": {
      // only OWN LAND units board (a ship can't ride a ship); the carrier must be
      // a real, FINISHED garrison holder (a transport ship OR a built tower — not a
      // half-constructed tower foundation).
      if (!owns(world, cmd.player, cmd.transport) || !world.garrison.has(cmd.transport)) break;
      if (world.building.get(cmd.transport)?.isFoundation) break;
      const boarders: number[] = [];
      for (const eid of cmd.entities) {
        if (!owns(world, cmd.player, eid) || isNaval(world, eid) || !world.move.has(eid)) continue;
        setOrder(world, eid, { kind: "embark", transport: cmd.transport }, !!cmd.queued);
        boarders.push(eid);
      }
      // RENDEZVOUS: sail the transport to the coast nearest the boarding units, so
      // the ship and the units move TOWARD each other (they meet at the shore)
      // instead of the player having to manually dock the ship first.
      if (boarders.length) {
        let cx = 0, cy = 0, k = 0;
        for (const e of boarders) { const t = world.transform.get(e); if (t) { cx += t.pos.x; cy += t.pos.y; k++; } }
        if (k) {
          const r = nearestWaterEdge(world, cx / k, cy / k);
          if (r) setOrder(world, cmd.transport, { kind: "move", x: r.x, y: r.y }, !!cmd.queued);
        }
      }
      break;
    }
    case "unload": {
      if (!owns(world, cmd.player, cmd.transport) || !world.garrison.has(cmd.transport)) break;
      // a stationary garrison BUILDING (tower) can't sail to a drop point — eject
      // its occupants onto the land around it right away. A transport SHIP gets an
      // unload order and the transport system disgorges once it reaches the coast.
      if (!world.move.has(cmd.transport)) ejectGarrison(world, reg, cmd.transport, cmd.x, cmd.y);
      else setOrder(world, cmd.transport, { kind: "unload", x: cmd.x, y: cmd.y }, !!cmd.queued);
      break;
    }
  }
  // expose helper on world for gather/production systems
  (world as any)._nearestDropSite = nearestDropSite;
}
